import { useState, useRef, useEffect } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/partnerProgramPage.module.scss';
import tableStyles from '../css/table.module.scss';
import { Link } from 'react-router-dom';
import Pagination from '../components/pagination';
import { createPortal } from 'react-dom';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import CustomCalendar from '../components/customCalendar';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

interface EsimPurchase {
  _id: string;
  order_number: number;
  order_uuid: string;
  user_email: string;
  package_name: string;
  country_name: string;
  country_flag: string;
  purchase_date: string;
  purchase_price: string;
  purchase_currency: string;
  final_price: string;
  discount_amount: string;
  discount_code: string;
  iccid: string;
  sm_dp_address: string;
  activation_code: string;
  qr_code: string;
}

interface EsimPurchasesData {
  data: EsimPurchase[];
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

interface Statistics {
  totalPurchases: number;
  totalRevenue: number;
  totalDiscounts: number;
  uniqueCustomers: number;
  totalTopUps: number;
}

interface FetchedData {
  purchases: EsimPurchasesData;
  statistics: Statistics;
}

const EsimPurchasesAdmin = () => {
  const { user } = useAuth();
  const [view, setView] = useState<'purchases' | 'statistics'>('purchases');
  const [isActionOpen, setIsActionOpen] = useState<number | string | null>(null);
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dateValues, setDateValues] = useState<any[]>([null, null]);
  const { t } = useTranslation();

  const [sortByPrice, setSortByPrice] = useState<{ value: string; label: string }>({
    value: 'desc',
    label: t('esimAdmin.labels.descending'),
  });
  const [filterSortByPriceOpen, setFilterSortByPriceOpen] = useState<boolean>(false);

  const [sortByCountry, setSortByCountry] = useState<{ value: string; label: string }>({
    value: 'all',
    label: t('esimAdmin.labels.allCountries'),
  });
  const [filterSortByCountryOpen, setFilterSortByCountryOpen] = useState<boolean>(false);

  const filterSortByPriceDropRef = useRef<HTMLDivElement>(null);
  const filterSortByCountryDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dateRangeRef = useRef(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchedData, setFetchedData] = useState<FetchedData | null>(null);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${EnvPaths.SERVER_URL}/admin/get-all-esim-purchases?page=${currentPage}&q=${searchTerm}&sortByPrice=${sortByPrice.value}&country=${sortByCountry.value}&dateFrom=${formatDateForBackend(dateValues[0])}&dateTo=${formatDateForBackend(dateValues[1])}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
      setFetchedData(response.data);

      if (response.data.purchases.data.length > 0) {
        const uniqueCountries = Array.from(new Set(response.data.purchases.data.map((item: EsimPurchase) => item.country_name))).map((country) => ({
          value: country as string,
          label: country as string,
        }));
        setCountries([{ value: 'all', label: t('esimAdmin.labels.allCountries') }, ...uniqueCountries]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsActionOpen(null);
      setIsLoading(false);
    }
  };

  const handleActionClick = (id: number | string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(filterSortByPriceDropRef, () => {
    setFilterSortByPriceOpen(false);
  });

  const filterSortByPriceOptions = [
    { value: 'asc', label: t('esimAdmin.labels.ascending') },
    { value: 'desc', label: t('esimAdmin.labels.descending') },
  ];

  const handleSortByPriceChange = (item: { value: string; label: string }) => {
    setSortByPrice(item);
    setFilterSortByPriceOpen(false);
  };

  useClickAway(filterSortByCountryDropRef, () => {
    setFilterSortByCountryOpen(false);
  });

  const handleSortByCountryChange = (item: { value: string; label: string }) => {
    setSortByCountry(item);
    setFilterSortByCountryOpen(false);
  };

  const handleDateChange = (value: any) => {
    setDateValues(value);
  };

  const handleDateOnCancel = () => {
    setDateValues([null, null]);
  };

  const formatDateForBackend = (date: any) => {
    if (date && date.toDate) {
      return date?.toDate()?.toISOString()?.split('T')[0];
    }
    return '';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  useEffect(() => {
    fetchData();
  }, [user, currentPage, sortByPrice, sortByCountry, dateValues, searchTerm]);

  useEffect(() => {
    setSortByPrice((prev) => ({ ...prev, label: t('esimAdmin.labels.descending') }));
    setSortByCountry((prev) => ({ ...prev, label: t('esimAdmin.labels.allCountries') }));
  }, [t, i18next]);

  const handleCSVExport = async () => {
    try {
      const response = await fetch(`${EnvPaths.SERVER_URL}/admin/download-esim-purchases`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `esim_purchases_${new Date().toISOString().split('T')[0]}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
      toast.success(t('esimAdmin.success.exportSuccess'));
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('esimAdmin.errors.exportFailed'));
    }
  };

  const handleCopyQRCode = async (qrCode: string) => {
    try {
      await navigator.clipboard.writeText(qrCode);
      toast.success(t('esimAdmin.success.qrCodeCopied'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('esimAdmin.errors.copyFailed'));
    }
  };

  const handleViewUser = (userEmail: string) => {
    window.location.href = `/dashboard/users?email=${userEmail}`;
  };

  useClickAway(actionDropdownRef, () => {
    setIsActionOpen(null);
  });

  return (
    <main className={styles.pageContainer}>
      <div className={globalPageStyle.pageFlexContainer} style={{ height: '100%' }}>
        {/* Header - Tabs */}
        <div className={globalPageStyle.tabs}>
          <div className={`${globalPageStyle.tab} ${view === 'purchases' ? globalPageStyle.active : ''}`} onClick={() => setView('purchases')}>
            {t('esimAdmin.tabs.purchases')}
          </div>
          <div className={`${globalPageStyle.tab} ${view === 'statistics' ? globalPageStyle.active : ''}`} onClick={() => setView('statistics')}>
            {t('esimAdmin.tabs.statistics')}
          </div>
        </div>

        {user && user.role === 'admin' ? (
          isLoading ? (
            <div className="loader" />
          ) : (
            <div style={{ overflow: 'auto' }}>
              <div className={styles.pageContent}>
                {/* View Purchases */}
                {view === 'purchases' && (
                  <>
                    {/* Filters */}
                    <div className={styles.header}>
                      <div className={styles.headerLeft}>
                        <div className={styles.flexRow}>
                          <CustomSearchInput defaultValue={searchTerm} onSearch={setSearchTerm} icon="/assets/images/search-icon.svg" placeholder={t('esimAdmin.searchPlaceholder')} />
                          <CustomDropdown
                            ref={filterSortByPriceDropRef}
                            isOpen={filterSortByPriceOpen}
                            onToggle={() => setFilterSortByPriceOpen(!filterSortByPriceOpen)}
                            options={filterSortByPriceOptions}
                            value={sortByPrice}
                            onChange={handleSortByPriceChange}
                            width="255px"
                            label={t('esimAdmin.sortByPrice')}
                          />
                          <CustomDropdown
                            ref={filterSortByCountryDropRef}
                            isOpen={filterSortByCountryOpen}
                            onToggle={() => setFilterSortByCountryOpen(!filterSortByCountryOpen)}
                            options={countries}
                            value={sortByCountry}
                            onChange={handleSortByCountryChange}
                            width="255px"
                            label={t('esimAdmin.filterByCountry')}
                          />
                          {/* <CustomCalendar 
                            dateValues={dateValues} 
                            handleDateChange={handleDateChange} 
                            onCancel={handleDateOnCancel} 
                            ref={dateRangeRef} 
                          /> */}
                        </div>
                      </div>
                      <button type="button" className="primaryButton inverse" onClick={handleCSVExport}>
                        {t('esimAdmin.exportCsv')}
                      </button>
                    </div>

                    {/* Table */}
                    <div className={styles.tableBody}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('esimAdmin.orderNumber')}</th>
                              <th>{t('esimAdmin.customerEmail')}</th>
                              <th>{t('esimAdmin.package')}</th>
                              <th>{t('esimAdmin.country')}</th>
                              <th>{t('esimAdmin.purchaseDate')}</th>
                              <th>{t('esimAdmin.price')}</th>
                              {/* <th>{t('esimAdmin.discount')}</th> */}
                              <th className={tableStyles.actionColumn}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fetchedData?.purchases?.data?.map((item) => (
                              <tr key={item._id}>
                                <td>#{item.order_number}</td>
                                <td>{item.user_email}</td>
                                <td>{item.package_name}</td>
                                <td>
                                  {item.country_flag} {item.country_name}
                                </td>
                                <td>{formatDate(item.purchase_date)}</td>
                                <td>
                                  {item.purchase_currency}
                                  {item.final_price}
                                </td>
                                {/* <td>
                                  {item.discount_amount !== '0.00' 
                                    ? `${item.purchase_currency}${item.discount_amount}` 
                                    : '-'
                                  }
                                </td> */}
                                <td className={tableStyles.actionColumn}>
                                  <div className={tableStyles.actionShape} ref={buttonRef}>
                                    <img
                                      src="/assets/images/dots.svg"
                                      alt="dots"
                                      onClick={(e) => {
                                        const buttonElement = e.currentTarget as HTMLDivElement;
                                        handleActionClick(item._id, buttonElement);
                                      }}
                                    />
                                    {isActionOpen === item._id &&
                                      typeof window !== 'undefined' &&
                                      createPortal(
                                        <div
                                          ref={actionDropdownRef}
                                          className={`${tableStyles.actionShapeDropdown}`}
                                          style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`,
                                          }}
                                        >
                                          {/* <div className={tableStyles.actionShapeDropdownItem} onClick={() => handleViewUser(item.user_email)}>
                                            {t('esimAdmin.viewCustomer')}
                                          </div>
                                          <div className={tableStyles.actionShapeDropdownItem} onClick={() => handleCopyQRCode(item.qr_code)}>
                                            {t('esimAdmin.copyQRCode')}
                                          </div> */}
                                          <div className={tableStyles.actionShapeDropdownItem}>
                                            <Link to={`/dashboard/esim-purchases/${item._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                              {t('esimAdmin.viewDetails')}
                                            </Link>
                                          </div>
                                        </div>,
                                        document.body
                                      )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pagination */}
                    <Pagination onPrevious={() => setCurrentPage((prev) => prev - 1)} onNext={() => setCurrentPage((prev) => prev + 1)} currentPage={currentPage} totalPages={fetchedData?.purchases.pagination.totalPages ?? 1} />
                  </>
                )}

                {/* View Statistics */}
                {view === 'statistics' && (
                  <>
                    <div className={globalPageStyle.statistics}>
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('esimAdmin.totalPurchases')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData?.statistics?.totalPurchases ?? 0}</p>
                          </div>
                        </div>
                      </div>

                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('esimAdmin.totalTopUps')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData?.statistics?.totalTopUps ?? '0'}</p>
                          </div>
                        </div>
                      </div>

                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('esimAdmin.totalRevenue')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>€{fetchedData?.statistics?.totalRevenue?.toFixed(2) ?? '0.00'}</p>
                          </div>
                        </div>
                      </div>

                      {/* <div className={globalPageStyle.statisticsItem}>
                        <p>{t('esimAdmin.totalDiscounts')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>€{fetchedData?.statistics?.totalDiscounts?.toFixed(2) ?? '0.00'}</p>
                          </div>
                        </div>
                      </div> */}

                      {/* <div className={globalPageStyle.statisticsItem}>
                        <p>{t('esimAdmin.uniqueCustomers')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData?.statistics?.uniqueCustomers ?? 0}</p>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        ) : (
          <h1>{t('esimAdmin.noPermission')}</h1>
        )}
      </div>
    </main>
  );
};

export default EsimPurchasesAdmin;
